import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, Trash2 } from "lucide-react";
import { VERGOEDINGSTYPES, WERK_TYPES, fileUrl, PROVINCIES } from "@/lib/jobOptions";

export function JobForm({ job, setJob, editingId, createJob, resetJobForm, setTab, uploading, uploadFile, removeBijlage, toggleJobType }) {
  return (
    <form onSubmit={createJob} className="max-w-2xl bg-white border border-slate-200 rounded-md p-6 space-y-4">
      {editingId && (
        <div data-testid="edit-mode-banner" className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-sm px-3 py-2">
          <span className="text-sm font-semibold text-orange-700">Je past een bestaande opdracht aan.</span>
          <button type="button" data-testid="cancel-edit-btn" onClick={() => { resetJobForm(); setTab("opdrachten"); }}
            className="text-xs font-bold text-slate-500 hover:text-red-600">Annuleren</button>
        </div>
      )}
      <div>
        <Label className="text-xs font-bold text-slate-700">Titel</Label>
        <Input data-testid="job-titel" required value={job.titel} onChange={(e) => setJob({ ...job, titel: e.target.value })} className="rounded-sm border-slate-300" placeholder="Montage kunststof kozijnen nieuwbouw" />
      </div>
      <div>
        <Label className="text-xs font-bold text-slate-700">Beschrijving</Label>
        <Textarea data-testid="job-beschrijving" required value={job.beschrijving} onChange={(e) => setJob({ ...job, beschrijving: e.target.value })} className="rounded-sm border-slate-300" rows={4} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-bold text-slate-700">Locatie</Label>
          <Input data-testid="job-locatie" required value={job.locatie} onChange={(e) => setJob({ ...job, locatie: e.target.value })} className="rounded-sm border-slate-300" placeholder="bv. Brugge" />
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-700">Provincie van de werf</Label>
          <Select value={job.provincie} onValueChange={(v) => setJob({ ...job, provincie: v })}>
            <SelectTrigger data-testid="job-provincie" className="rounded-sm border-slate-300">
              <SelectValue placeholder="Kies provincie" />
            </SelectTrigger>
            <SelectContent>
              {PROVINCIES.map((g) => (
                <div key={g.groep}>
                  <div className="px-2 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wide">{g.groep}</div>
                  {g.opties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-700">Startdatum</Label>
          <Input data-testid="job-startdatum" required value={job.start_datum} onChange={(e) => setJob({ ...job, start_datum: e.target.value })} className="rounded-sm border-slate-300" placeholder="bv. 15 juli 2026" />
        </div>
      </div>

      <div>
        <Label className="text-xs font-bold text-slate-700 mb-2 block">Type werk <span className="font-normal text-slate-400">(meerdere mogelijk)</span></Label>
        <div className="grid sm:grid-cols-2 gap-2">
          {WERK_TYPES.map((t) => (
            <label key={t.value} className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 border border-slate-200 rounded-sm px-3 py-2 hover:border-slate-400">
              <input type="checkbox" data-testid={`job-type-${t.value}`}
                checked={(job.types || []).includes(t.value)} onChange={() => toggleJobType(t.value)}
                className="w-4 h-4 accent-orange-500 flex-shrink-0" />
              <span>{t.label}</span>
            </label>
          ))}
        </div>
        <Input data-testid="job-type-andere" value={job.type_andere} onChange={(e) => setJob({ ...job, type_andere: e.target.value })}
          className="rounded-sm border-slate-300 mt-2" placeholder="Andere (vul zelf in)" />
      </div>

      <div>
        <Label className="text-xs font-bold text-slate-700">Vergoeding</Label>
        <Select value={job.vergoedingstype} onValueChange={(v) => setJob({ ...job, vergoedingstype: v })}>
          <SelectTrigger data-testid="job-vergoedingstype" className="rounded-sm border-slate-300 max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {VERGOEDINGSTYPES.map((v) => (
              <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-400 mt-1">Het bedrag wordt onderling afgesproken met de professional.</p>
      </div>

      <div>
        <Label className="text-xs font-bold text-slate-700 mb-2 block">Bijlagen <span className="font-normal text-slate-400">(PDF, PNG of GIF — meerdere mogelijk)</span></Label>
        <div className="space-y-2">
          {job.bijlagen.map((b) => (
            <div key={b.id} data-testid="bijlage-item" className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
              <a href={fileUrl(b.id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-700 hover:text-orange-600 truncate">
                <Paperclip className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{b.naam}</span>
              </a>
              <button type="button" data-testid="remove-bijlage-btn" onClick={() => removeBijlage(b.id)} className="text-slate-400 hover:text-red-600 flex-shrink-0 ml-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <label className="mt-2 inline-flex items-center gap-2 cursor-pointer border border-dashed border-slate-300 rounded-sm px-4 py-2 text-sm font-semibold text-slate-600 hover:border-orange-400 hover:text-orange-600">
          <Paperclip className="w-4 h-4" />
          {uploading ? "Bezig met uploaden..." : job.bijlagen.length ? "Nog een bestand toevoegen" : "Bestand toevoegen"}
          <input data-testid="bijlage-input" type="file" accept=".pdf,.png,.gif,application/pdf,image/png,image/gif" onChange={uploadFile} disabled={uploading} className="hidden" />
        </label>
      </div>

      <Button data-testid="submit-job-btn" type="submit" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
        {editingId ? "Opdracht bijwerken" : "Opdracht plaatsen"}
      </Button>
    </form>
  );
}
